import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { TicketPercent, Plus, Loader2, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const isPrivilegedRole = (r) => ["super_admin", "sysadmin", "owner"].includes(r);

const inputCls = "w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60 transition";
const labelCls = "block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1.5";

export default function AdminPromoCodes() {
  const { user } = useAuth();
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    code: "", type: "percent", value: "", duration: "once",
    duration_in_months: "", max_redemptions: "", expires_at: "",
  });

  const load = useCallback(() => {
    api.get("/admin/promo-codes")
      .then((r) => setCodes(r.data?.promo_codes || []))
      .catch(() => setCodes([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!user) return null;
  if (!isPrivilegedRole(user.role)) return <Navigate to="/dashboard" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.value) { toast.error("Code and value are required"); return; }
    setBusy(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        duration: form.duration,
        ...(form.type === "percent"
          ? { percent_off: Number(form.value) }
          : { amount_off_usd: Number(form.value) }),
        ...(form.duration === "repeating" && form.duration_in_months
          ? { duration_in_months: Number(form.duration_in_months) } : {}),
        ...(form.max_redemptions ? { max_redemptions: Number(form.max_redemptions) } : {}),
        ...(form.expires_at ? { expires_at: form.expires_at } : {}),
      };
      await api.post("/admin/promo-codes", payload);
      toast.success(`Promo code ${payload.code} created`);
      setForm({ code: "", type: "percent", value: "", duration: "once", duration_in_months: "", max_redemptions: "", expires_at: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create promo code");
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async (code) => {
    try {
      await api.post(`/admin/promo-codes/${code}/deactivate`);
      toast.success(`${code} deactivated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to deactivate");
    }
  };

  const discountLabel = (p) =>
    p.percent_off ? `${p.percent_off}% off` : `$${Number(p.amount_off_usd || 0).toFixed(2)} off`;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto animate-fade-up" data-testid="admin-promo-codes-page">
      <PageHeader
        eyebrow="// ADMIN"
        title="Promo Codes"
        description="Create discount codes customers can redeem at checkout — launches, partners, win-backs."
      />

      <form onSubmit={handleCreate} className="rounded-xl border border-line bg-bg-surface p-6 mb-8" data-testid="promo-create-form">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Code</label>
            <input value={form.code} onChange={set("code")} placeholder="LAUNCH25" className={`${inputCls} uppercase`} data-testid="promo-code-input" />
          </div>
          <div>
            <label className={labelCls}>Discount type</label>
            <select value={form.type} onChange={set("type")} className={inputCls} data-testid="promo-type-select">
              <option value="percent">Percent off</option>
              <option value="amount">Dollar amount off</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>{form.type === "percent" ? "Percent (1–100)" : "Amount (USD)"}</label>
            <input type="number" min="1" step="any" value={form.value} onChange={set("value")}
                   placeholder={form.type === "percent" ? "25" : "10"} className={inputCls} data-testid="promo-value-input" />
          </div>
          <div>
            <label className={labelCls}>Applies</label>
            <select value={form.duration} onChange={set("duration")} className={inputCls} data-testid="promo-duration-select">
              <option value="once">First payment only</option>
              <option value="repeating">For N months</option>
              <option value="forever">Every payment forever</option>
            </select>
          </div>
          {form.duration === "repeating" && (
            <div>
              <label className={labelCls}>Months</label>
              <input type="number" min="1" value={form.duration_in_months} onChange={set("duration_in_months")}
                     placeholder="3" className={inputCls} data-testid="promo-months-input" />
            </div>
          )}
          <div>
            <label className={labelCls}>Max redemptions <span className="normal-case">(optional)</span></label>
            <input type="number" min="1" value={form.max_redemptions} onChange={set("max_redemptions")}
                   placeholder="100" className={inputCls} data-testid="promo-max-input" />
          </div>
          <div>
            <label className={labelCls}>Expires <span className="normal-case">(optional)</span></label>
            <input type="date" value={form.expires_at} onChange={set("expires_at")} className={inputCls} data-testid="promo-expiry-input" />
          </div>
        </div>
        <button type="submit" disabled={busy}
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2 text-black font-semibold text-sm hover:bg-brand-amber/90 disabled:opacity-60 transition"
                data-testid="promo-create-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Create code
        </button>
      </form>

      {codes === null ? (
        <div className="flex items-center gap-2 py-12 justify-center text-ink-muted">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading codes…
        </div>
      ) : codes.length === 0 ? (
        <div className="rounded-xl border border-line bg-bg-surface p-10 text-center" data-testid="promo-empty">
          <TicketPercent className="h-10 w-10 mx-auto text-ink-muted mb-3" />
          <p className="text-sm text-ink-secondary">No promo codes yet — create your first one above.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-line bg-bg-surface overflow-hidden" data-testid="promo-codes-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Discount</th>
                <th className="px-4 py-3">Applies</th>
                <th className="px-4 py-3 text-right">Used</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((p) => (
                <tr key={p.code} className="border-b border-line/50 last:border-0" data-testid={`promo-row-${p.code}`}>
                  <td className="px-4 py-3 font-mono font-bold text-brand-amber">{p.code}</td>
                  <td className="px-4 py-3">{discountLabel(p)}</td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {p.duration === "once" ? "First payment" : p.duration === "forever" ? "Forever" : `${p.duration_in_months} months`}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {p.times_redeemed || 0}{p.max_redemptions ? ` / ${p.max_redemptions}` : ""}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">{p.expires_at || "Never"}</td>
                  <td className="px-4 py-3">
                    {p.active ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-ink-muted text-xs font-semibold"><Ban className="h-3.5 w-3.5" /> Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {p.active && (
                      <button onClick={() => handleDeactivate(p.code)}
                              className="text-xs text-red-400 hover:text-red-300 font-medium transition"
                              data-testid={`promo-deactivate-${p.code}`}>
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-ink-muted mt-4">
        Customers enter these codes on the Stripe checkout page ("Add promotion code"), or you can link them into campaigns.
      </p>
    </div>
  );
}
